import useApi from "../../../../Utils/BackendClient";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsMobile } from "../../../../Utils/ResizeUtils";
import { Translate } from "../../../../Utils/TranslationUtils";

import CommonToolbar from "../../../UIElements/CommonToolbar";

import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";

import commonStyle from "../../CommonStyles.module.css";
import { DisplayErrors } from "../../../../Utils/ErrorHandler";

const NewUserRole = () => {

    const isMobile = useIsMobile();
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useRef(null);

    const { createUserRole, fetchPermissions, fetchUserRoleDetails, updateUserRole } = useApi();
    
    const [isEdit, setIsEdit] = useState(false);
    const [permissions, setPermissions] = useState([]);
    const [roleName, setRoleName] = useState("");
    const [submitLoading, setSubmitLoading] = useState(false);

    useEffect(() => {
        const getRolePermissions = async(id) => {

            const response = await fetchUserRoleDetails(id);
            if(response.status === 200)
            {
                setRoleName(response.data.name);
                if(response.data != null && (response.data.permissions != null && response.data.permissions.length > 0)){
                    return response.data.permissions;
                }
                else{
                    return [];
                }
            }
            else{
                toast.current.show({ severity: "error", summary: "Помилка отримання пермісій", detail: "Пермісії для ролі не було отримано", life: 2000 })
                return [];
            }
        }

        const fetchData = async () => {
            
            const response = await fetchPermissions();

            if(location.state != null){
                setIsEdit(true);
                const appliedPermissions = await getRolePermissions(location.state.id);

                if(appliedPermissions.length > 0){
                    response.data.forEach(permission => {
                        if(appliedPermissions.some(item => item.codename === permission.codename)){
                            permission.allowed = true;
                        }
                    }); 
                }
                else{
                    response.data.forEach(permission => {
                        permission.allowed = false;
                    });
                }
            }
            else{
                response.data.forEach(permission => {
                    permission.allowed = false;
                });
            }

            setPermissions(response.data);
        }

        fetchData();
    }, [fetchPermissions, fetchUserRoleDetails, location]);

    const formik = useFormik({
        initialValues: {
            name: roleName,
            permissions: permissions
        },
        enableReinitialize: true,
        validate: values => {
            const errors = [];
            if (values.name === null || values.name === "") {
                errors.name = "Введіть назву ролі";
            }
            return errors;
        },
        onSubmit: async (values) => {

            setSubmitLoading(true);

            const requestBody = {
                name: values.name,
                permissions: []
            };
            values.permissions.forEach(permission => {
                const allowedPermission = {};
                if (permission.allowed) {
                    allowedPermission.codename = permission.codename;
                    requestBody.permissions.push(allowedPermission);
                }
            });

            const response = !isEdit ? await createUserRole(requestBody) : await updateUserRole(location.state.id, requestBody);

            setSubmitLoading(false);

            const successCode = isEdit ? 200 : 201;

            if (response.status === successCode) {
                const toastMessage = { severity: "success", summary: isEdit ? "Успіх редагування" : "Успіх створення", detail: "Роль " + response.data.name + (isEdit ? " успішно відредаговано" : " успішно створено"), life: 3000 }
                navigate('/roles', { state: { toast: toastMessage } });
            }
            else {
                DisplayErrors(response.error, toast);
            }
        }
    });

    const toolbarLeftData = () => {

        let name = "Створення нової ролі користувача"
        if(isEdit){
            name = "Редагування ролі користувача " + formik.values.name;
        }

        return !isMobile ? <h3>{name}</h3> : null;
    }

    const groupByApp = (permissions) => {
        return permissions.reduce((acc, permission) => {
            const { app } = permission;
            if (!acc[app]) {
                acc[app] = [];
            }
            acc[app].push(permission);
            return acc;
        }, {});
    };

    const PermissionsList = ({ permissions }) => {
        const groupedPermissions = groupByApp(permissions);

        const handleRadioChange = (permissionId) => {
            const updatedPermissions = permissions.map(permission => {
                if (permission.id === permissionId) {
                    return { ...permission, allowed: !permission.allowed }; // Toggle allowed state
                }
                return permission;
            });

            // Update Formik's state
            formik.setFieldValue('permissions', updatedPermissions);
        };

        const handleGroupRadioChange = (app) => {
            const isChecked = isGroupChecked(app);
            permissions.forEach(element => {
                if(element.app === app){
                    element.allowed = !isChecked;
                }
            });

            formik.setFieldValue("permissions", permissions);
        }

        const isGroupChecked = (app) => {
            let isChecked = true;
            permissions.forEach(element => {
                if(element.app === app){
                    if(!element.allowed){
                        isChecked = false;
                    }
                }
            });

            return isChecked;
        }

        return (
            <div>
                {Object.keys(groupedPermissions).map(app => (<>
                    <div key={"group" + app} className="flex flex-row align-items-center">
                        <Checkbox 
                        className="mr-2" 
                        checked={isGroupChecked(app)}
                        onChange={() => handleGroupRadioChange(app)} />
                        <h3>{Translate(app)}</h3>
                    </div>
                    <Divider className="mt-0"/>
                    <div key={app} className="grid mb-4">
                        {groupedPermissions[app].map(permission => (
                            <div className="col-4 flex align-items-center" key={permission.id}>
                                <Checkbox
                                    checked={permission.allowed}
                                    onChange={() => handleRadioChange(permission.id)}
                                />
                                <label className="ml-2">{permission.name}</label>
                            </div>
                        ))}
                    </div>
                    </>))}
            </div>
        );
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <Toast ref={toast} />
            <CommonToolbar isMobile={isMobile} isFinalized={false} loading={submitLoading} onRetunButtonClick={() => navigate("/roles")} leftContent={toolbarLeftData()}/>
            <div className="grid nested-grid">
                <div className={`${commonStyle.leftMenu} col-3`}>
                    <div className={commonStyle.formInput}>
                        <div className="p-inputgroup flex-column">
                            <FloatLabel>
                                <InputText
                                    name="name"
                                    value={formik.values.name}
                                    onChange={(e) => formik.setFieldValue('name', e.target.value)}
                                    className={formik.errors.name && formik.touched.name ? 'p-invalid' : ''}
                                />
                                <label>Назва ролі</label>
                            </FloatLabel>
                            {formik.errors.name && formik.touched.name && (<small className={commonStyle.errorSmall}>{formik.errors.name}</small>)}
                        </div>
                    </div>
                </div>
                <div className="col-9">
                    <PermissionsList permissions={formik.values.permissions} />
                </div>
            </div>
        </form>
    );
}

export default NewUserRole;